import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { fmtNumber } from "@/components/Amount";   // Batch 11 · B7: 2 decimals everywhere
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import { WS_OPEN_PARAM } from "@/lib/worksheetLinks";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { offerLink } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import DupGroupResolve from "@/components/DupGroupResolve";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Ref from "@/components/Ref";
import DownloadTemplateButton from "@/components/DownloadTemplateButton";
import { fmtDateTime } from "@/lib/dt";
import { fmtDate } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import SaveCancelBar from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";
import { useFormDraft, useTableDraft } from "@/lib/useDrafts";
import { saveRowFirst } from "@/lib/saveThenSend";   // STEP 11d · A9: Send saves a changed row first
import { confirmDiscard } from "@/lib/unsavedChanges";
import TbOnlyNotice, { isTbOnly } from "@/components/TbOnlyNotice";   // PR 3 · rule 1 (B-1a): matching needs journal lines

/**
 * Batch C · Round C1 · W5 — Completeness worksheet dialog.
 * Fix List 8 · R1 — after "Run GL match" the dialog polls the job and
 * renders per-row matched voucher/date/amount or "Not found in GL",
 * pre-fills recorded_in_period + amount_agrees from match_status
 * (auditor can override, logged), shows a summary line (matched /
 * unmatched counts & totals with run date DD-MMM-YYYY HH:mm), and
 * exposes "Send to misstatements" (Factual) on not-found rows.
 */
export default function CompletenessWorksheetDialog() {
  const { currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const ref = params.get(WS_OPEN_PARAM.completeness);
  const [doc, setDoc] = useState(null);
  const [matching, setMatching] = useState(false);
  const compCols = useResizableColumns({ storageKey: "completeness-rows", defaults: [110, 110, 200, 130, 150, 130, 130, 220, 120], mins: [90, 96, 120, 100, 100, 90, 90, 120, 90] });
  const pollRef = useRef(null);

  const fmtMoney = (n) => fmtNumber(n || 0);

  useEffect(() => {
    if (!ref || !currentEngId) { setDoc(null); return; }
    api.get(`/engagements/${currentEngId}/completeness/ref/${ref}`, { _silent: true })
      .then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, [ref, currentEngId]);

  // R1 (Fix List 8) — clear the poller when the dialog closes.
  useEffect(() => () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, []);

  const reload = async () => {
    try { const r = await api.get(`/engagements/${currentEngId}/completeness/ref/${ref}`); setDoc(r.data); return r.data; }
    catch { return null; }
  };
  // Batch 28 · Follow-up 1 · F2: the match window and the conclusion are a form draft (one Save / Cancel bar, one PATCH with the
  // changed fields); the listing rows are a table draft (one bar; Save sends only the changed rows, one PATCH each; a failed
  // row keeps its error). Upload / remove file, duplicates, send to Sampling, run match, send to Misstatements, mark complete
  // and the sign-offs stay immediate actions.
  const form = useFormDraft(doc);
  const [formSaving, setFormSaving] = useState(false);
  const td = useTableDraft({
    rows: doc ? asArray(doc.rows) : [],
    saveRow: (row_id, body) => api.patch(`/engagements/${currentEngId}/completeness/ref/${ref}/rows/${row_id}`, body),
    reload,
  });
  useEffect(() => { form.reset(); td.cancel(); }, [ref, currentEngId]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!ref) return null;
  const doClose = () => { const p = new URLSearchParams(params); p.delete(WS_OPEN_PARAM.completeness); setParams(p, { replace: true }); };
  const close = async () => { if (await confirmDiscard(form.dirty || td.dirty, lang)) doClose(); };
  const patch = async (body) => {
    try { const r = await api.patch(`/engagements/${currentEngId}/completeness/ref/${ref}`, body); setDoc(r.data); }
    catch (e) { toast.error(toErrMsg(e, "Save failed")); }
  };
  const saveForm = async () => {
    if (!form.dirty) return;
    setFormSaving(true);
    const body = { ...form.patch };
    if ("match_window_days" in body && body.match_window_days === "") body.match_window_days = 5;   // as the old on-blur default
    try { const r = await api.patch(`/engagements/${currentEngId}/completeness/ref/${ref}`, body); setDoc(r.data); form.reset(); }
    catch (e) { toast.error(toErrMsg(e, "Save failed")); }
    finally { setFormSaving(false); }
  };
  const fv = form.value || {};
  const runMatch = async () => {
    if (!hasListing) { toast.info(t("comp.uploadFirst")); return; }
    try {
      const resp = await api.post(`/engagements/${currentEngId}/completeness/ref/${ref}/match`);
      setMatching(true);
      toast.success(t("comp.matchQueued"));
      const jobId = resp.data?.job_id;
      // R1 — poll the job every 1s. When it lands (done/failed), reload
      // the worksheet so the new match_summary + per-row status appear
      // without a manual refresh.
      if (pollRef.current) clearInterval(pollRef.current);
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        ticks += 1;
        try {
          if (jobId) {
            const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
            const status = jr.data?.status;
            if (status === "done" || status === "failed" || status === "error") {
              clearInterval(pollRef.current); pollRef.current = null;
              await reload();
              setMatching(false);
              return;
            }
          }
          // Safety net — reload after ~15s even if job lookup failed.
          if (ticks % 5 === 0) await reload();
          if (ticks > 30) { clearInterval(pollRef.current); pollRef.current = null; setMatching(false); }
        } catch { /* keep polling */ }
      }, 1000);
    } catch (e) { toast.error(toErrMsg(e, "Match failed")); setMatching(false); }
  };
  const gate = doc?.can_mark_complete || { ok: false, reasons: [] };
  const hasListing = !!doc?.source_file?.document_id;
  const matchSummary = doc?.match_summary;

  return (
    <FullScreenDialog open={true} onClose={close} testid="completeness-dialog"
                      title={<>{t("comp.title")} — <Ref>{ref}</Ref></>}>
        {!doc ? (
          <div data-testid="comp-loading">{t("common.loading")}</div>
        ) : (
          <div className="space-y-3">
            <SignOffBar itemType="completeness" itemKey={ref} refreshKey={doc.updated_at} onChange={reload} saveFirst={form.dirty || td.dirty} />
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span data-testid="comp-fs">{t("comp.direction")}: <b>{doc.fs_code}</b></span>
              <div className="flex items-center gap-2">
                <Label className="text-xs">{t("comp.matchWindow")}</Label>
                <Input type="number" min="0" max="60" className="h-6 w-16 text-xs"
                        value={fv.match_window_days ?? 5}
                        onChange={(e) => { const v = parseInt(e.target.value, 10); form.set("match_window_days", isNaN(v) ? "" : v); }}
                        data-testid="comp-match-window" />
              </div>
            </div>
            {hasListing ? (
              <div className="text-xs text-emerald-700" data-testid="comp-file">
                {t("comp.sourceFile")}: {doc?.source_file?.file_count || 1} {t("inv6.filesLabel") || "file(s)"} · {doc.source_file.row_count} rows
                {doc?.source_file?.total_amount != null && (
                  <> · {t("comp.totalAmount") || "Total"}:{" "}
                     <span dir="ltr">{fmtNumber(doc.source_file.total_amount)}</span></>
                )}
                {doc?.source_file?.unresolved_duplicates > 0 && doc?.source_file?.total_amount_resolved != null
                  && Number(doc.source_file.total_amount_resolved) !== Number(doc.source_file.total_amount) && (
                  <span className="ms-2 text-amber-800">· excl. excluded dupes:{" "}
                    <span dir="ltr">{fmtNumber(doc.source_file.total_amount_resolved)}</span>
                  </span>
                )}
              </div>
            ) : (
              <div className="text-xs text-amber-800" data-testid="comp-nofile">{t("comp.uploadFirst")}</div>
            )}
            {/* Round 8 · F3 / F4 — multi-file list + duplicate banner
                for Completeness, matching the Inventory count
                worksheet pattern (F3 reported as missing). */}
            {doc?.source_file?.unresolved_duplicates > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900"
                   data-testid="comp-dup-banner">
                <b>{doc.source_file.unresolved_duplicates}</b>{" "}
                {t("comp.dupBanner") || "duplicate Doc No(s) across files"} ·
                <span dir="rtl">تكرارات في رقم المستند بين الملفات</span>
              </div>
            )}
            {/* Round 9 · F4 — per-group resolve controls for
                 Completeness. Mirror of the inventory block. */}
            {asArray(doc?.source_file?.duplicate_groups).map((g) => (
              <DupGroupResolve key={g.key} group={g}
                                 entity="completeness"
                                 engId={currentEngId} ref_={ref}
                                 onResolved={async () => {
                                   const fresh = await api.get(`/engagements/${currentEngId}/completeness/ref/${ref}`);
                                   setDoc(fresh.data);
                                 }} />
            ))}
            {asArray(doc?.source_files).filter((l) => !l.deleted_at).length > 0 && (
              <ul className="divide-y rounded border bg-white dark:bg-slate-900"
                  data-testid="comp-listing-files">
                {asArray(doc?.source_files).filter((l) => !l.deleted_at).map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-2 px-2 py-1 text-[11px]"
                      data-testid={`comp-listing-row-${l.id}`}>
                    <div className="min-w-0 flex-1 break-words">
                      <span className="font-mono">{l.filename}</span>
                      <span className="ms-2 text-muted-foreground">
                        {l.row_count} rows · {fmtDate(l.uploaded_at)}
                        {l.uploaded_by_name ? ` · ${l.uploaded_by_name}` : ""}
                      </span>
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 text-[11px] text-red-700"
                            data-testid={`comp-listing-remove-${l.id}`}
                            onClick={async () => {
                              if (!await askConfirm(t("comp.removeFileConfirm") || "Remove this file? It will go to the recycle bin.")) return;
                              try {
                                await api.delete(`/engagements/${currentEngId}/completeness/ref/${ref}/listings/${l.id}`);
                                const r = await api.get(`/engagements/${currentEngId}/completeness/ref/${ref}`);
                                setDoc(r.data);
                                toast.success(t("comp.removedOk") || "File moved to recycle bin");
                              } catch (e) { toast.error(toErrMsg(e, "Remove failed")); }
                            }}>
                      {t("common.remove") || "Remove"}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-2" data-testid="comp-upload-row">
              <Label htmlFor={`comp-upload-${ref}`} className="text-xs">
                {t("comp.upload")}
              </Label>
              <Button asChild size="sm" variant="outline">
                <label htmlFor={`comp-upload-${ref}`} className="cursor-pointer"
                       data-testid="comp-upload-button">
                  {t("common.chooseFile")}
                </label>
              </Button>
              <DownloadTemplateButton kind="completeness_source_listing"
                                        testid="comp-download-template" />
              <span className="text-xs text-slate-600"
                    data-testid="comp-upload-filename">
                {doc?.source_file?.filename || t("common.noFileChosen")}
              </span>
              <input id={`comp-upload-${ref}`} type="file" accept=".xlsx,.csv"
                     className="hidden" data-testid="comp-upload-input"
                     onChange={async (e) => {
                       const file = e.target.files?.[0];
                       if (!file) return;
                       try {
                         const buf = await file.arrayBuffer();
                         let bin = "";
                         const bytes = new Uint8Array(buf);
                         const chunk = 0x8000;
                         for (let i = 0; i < bytes.length; i += chunk) {
                           bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
                         }
                         const data_base64 = btoa(bin);
                         const r = await api.post(
                           `/engagements/${currentEngId}/completeness/ref/${ref}/upload-listing`,
                           { filename: file.name, data_base64 });
                         setDoc(r.data);
                         toast.success(t("comp.uploadOk"));
                       } catch (err) {
                         toast.error(toErrMsg(err, "Upload failed"));
                       } finally {
                         e.target.value = "";
                       }
                     }} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline"
                data-testid="comp-send-sampling"
                disabled={!hasListing
                           || Number(doc?.source_file?.row_count || 0) === 0}
                onClick={async () => {
                  try { await api.post(`/engagements/${currentEngId}/completeness/ref/${ref}/send-to-sampling`);
                    toast.success(t("comp.sentToSamplingOk"));
                  } catch (e) { toast.error(toErrMsg(e, "Send failed")); }
                }}>{t("comp.sendToSampling")}</Button>
              {hasListing && Number(doc?.source_file?.row_count || 0) === 0 && (
                <span className="text-xs text-amber-800"
                      data-testid="comp-send-sampling-zero-rows">
                  {t("headFolders.zeroRows")}
                </span>
              )}
              <Button size="sm" variant="outline"
                data-testid="comp-run-match"
                disabled={!hasListing || matching || isTbOnly(doc?.matching_status)}
                onClick={runMatch}>
                {matching ? t("comp.matching") : t("comp.runMatch")}
              </Button>
              {matching && (
                <span className="text-xs text-slate-600" data-testid="comp-matching-indicator">
                  {t("comp.matching")}
                </span>
              )}
            </div>
            <TbOnlyNotice status={doc?.matching_status} testid="comp-tb-only" />
            {/* R1 — match summary line under the actions */}
            {matchSummary && (
              <div className="rounded border bg-slate-50 p-2 text-xs" data-testid="comp-match-summary">
                <div>
                  {(t("comp.matchSummary") || "")
                    .replace("{matched}", String(matchSummary.matched_count ?? 0))
                    .replace("{matchedTotal}", fmtMoney(matchSummary.matched_total ?? 0))
                    .replace("{unmatched}", String(matchSummary.unmatched_count ?? 0))
                    .replace("{unmatchedTotal}", fmtMoney(matchSummary.unmatched_total ?? 0))}
                  {(matchSummary.amount_differs_count ?? 0) > 0 && (
                    <span data-testid="comp-match-differs"> · {(t("comp.matchSummaryDiffers") || "")
                      .replace("{n}", String(matchSummary.amount_differs_count))
                      .replace("{total}", fmtMoney(matchSummary.amount_differs_total ?? 0))}</span>
                  )}
                </div>
                <div className="text-slate-500" data-testid="comp-match-runat">
                  {(t("comp.matchRunAt") || "").replace("{date}", fmtDateTime(matchSummary.run_at))}
                </div>
              </div>
            )}
            <div className="overflow-auto rounded border">
              <ResizableTable cols={compCols} className="w-full text-xs" data-testid="comp-rows">
                <thead className="bg-slate-50"><tr>
                  <ResizableTh index={0} className="p-2 text-start">{t("comp.colDocNo")}</ResizableTh>
                  <ResizableTh index={1} className="p-2 text-start">{t("common.date")}</ResizableTh>
                  <ResizableTh index={2} className="p-2 text-start">{t("comp.colParty")}</ResizableTh>
                  <ResizableTh index={3} className="p-2 text-end">{t("common.amount")}</ResizableTh>
                  <ResizableTh index={4} className="p-2 text-start">{t("comp.matchedVoucher")}</ResizableTh>
                  <ResizableTh index={5} className="p-2 text-start">{t("comp.recordedInPeriod")}</ResizableTh>
                  <ResizableTh index={6} className="p-2 text-start">{t("comp.amountAgrees")}</ResizableTh>
                  <ResizableTh index={7} className="p-2 text-start">{t("comp.remarks")}</ResizableTh>
                  <ResizableTh index={8} className="p-2 text-start">{t("ob.action")}</ResizableTh>
                </tr></thead>
                <tbody>
                  {(doc.rows || []).length === 0 && (
                    <tr><td colSpan={9} className="p-2 text-center text-muted-foreground" data-testid="comp-no-rows">—</td></tr>
                  )}
                  {asArray(doc.rows).map((r0) => {
                    const r = td.rowValue(r0);
                    const isNotFound = r.match_status === "not_found";
                    const amountDiffers = r.match_status === "voucher_amount_differs";   // Batch 25 · C9
                    const isMatched = (r.match_status === "matched" || amountDiffers) && r.match;
                    return (
                    <tr key={r.id} data-testid={`comp-row-${r.id}`} data-changed={td.isChanged(r.id) ? "1" : "0"} data-failed={td.errorOf(r.id) ? "1" : "0"}
                         className={td.rowClass(r.id) || (isNotFound || amountDiffers ? "bg-amber-50" : "")}>
                      <td className="p-2" dir="ltr"><bdi>{r.doc_no}</bdi><RowSaveError draft={td} id={r.id} testid={`comp-row-error-${r.id}`} /></td>
                      <td className="p-2" dir="ltr"><bdi>{r.date}</bdi></td>
                      <td className="p-2">{r.party}</td>
                      <td className="p-2 text-end" dir="ltr">{fmtMoney(r.amount)}</td>
                      <td className="p-2" data-testid={`comp-match-${r.id}`}>
                        {isMatched ? (
                          <div className="text-xs" dir="ltr">
                            <bdi>{r.match.voucher_no || "—"}</bdi>
                            {r.match.entry_date ? <> · <bdi>{fmtDate(r.match.entry_date)}</bdi></> : null}
                            {" · "}
                            <bdi>{fmtMoney(r.match.amount)}</bdi>
                            {amountDiffers && <div className="text-amber-800" dir="auto" data-testid={`comp-amount-differs-${r.id}`}>{t("comp.voucherAmountDiffers")}</div>}
                          </div>
                        ) : isNotFound ? (
                          <span className="text-xs text-red-700" data-testid={`comp-not-found-${r.id}`}>
                            {t("comp.notFound")}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-2">
                        <select data-testid={`comp-recorded-${r.id}`}
                          value={r.recorded_in_period === true ? "true" : r.recorded_in_period === false ? "false" : ""}
                          onChange={(e) => td.edit(r.id, "recorded_in_period", e.target.value === "true" ? true : e.target.value === "false" ? false : null)}>
                          <option value="">—</option>
                          <option value="true">{t("common.yes")}</option>
                          <option value="false">{t("common.no")}</option>
                        </select>
                      </td>
                      <td className="p-2">
                        <select data-testid={`comp-agrees-${r.id}`}
                          value={r.amount_agrees === true ? "true" : r.amount_agrees === false ? "false" : ""}
                          onChange={(e) => td.edit(r.id, "amount_agrees", e.target.value === "true" ? true : e.target.value === "false" ? false : null)}>
                          <option value="">—</option>
                          <option value="true">{t("common.yes")}</option>
                          <option value="false">{t("common.no")}</option>
                        </select>
                      </td>
                      <td className="p-2">
                        <Input className="h-6 text-xs" placeholder={t("comp.remarks")}
                          data-testid={`comp-remarks-${r.id}`}
                          value={r.remarks || ""}
                          onChange={(e) => td.edit(r.id, "remarks", e.target.value)} />
                      </td>
                      <td className="p-2">
                        {r.misstatement_sent ? (
                          <a href={r.misstatement_sent.link_back || "#"} target="_blank" rel="noopener"
                             className="inline-flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800"
                             data-testid={`comp-sent-ms-${r.id}`}>
                            <span>{t("comp.sentToMsOk")}</span>
                            <span className="underline" data-testid={`comp-sent-ms-link-${r.id}`}>
                              {t("common.viewSent")}
                            </span>
                          </a>
                        ) : (r0.recorded_in_period === false || r0.amount_agrees === false || isNotFound) && (
                          <Button size="sm" variant="outline" data-testid={`comp-send-ms-${r.id}`}
                            onClick={async () => {
                              if (!(await saveRowFirst(td, r.id, lang))) return;      // STEP 11d · A9 (find 30)
                              try { await api.post(`/engagements/${currentEngId}/completeness/ref/${ref}/send-to-misstatements`,
                                { row_id: r.id, amount: r.amount, party: r.party || "", doc_no: r.doc_no || "",
                                  description: `Completeness exception (${ref}) — ${r.doc_no}${isNotFound ? " · not found in GL" : ""}` });
                                toast.success(t("comp.sentToMsOk"));
                                await reload();
                              } catch (e) {      // Batch 29 · M65: already in Misstatements → offer the link
                                try { if (await offerLink(e, { engId: currentEngId, lang })) { await reload(); return; } }
                                catch (e2) { toast.error(toErrMsg(e2, "Send failed")); return; }
                                toast.error(toErrMsg(e, "Send failed"));
                              }
                            }}>{t("comp.sendToMisstatements")}</Button>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </ResizableTable>
            </div>
            <TableSaveBar draft={td} lang={lang} testid="comp-table-save" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">{t("comp.conclusion")}</Label>
                <Textarea data-testid="comp-conclusion" value={fv.conclusion || ""}
                  onChange={(e) => form.set("conclusion", e.target.value)} />
              </div>
            </div>
            <SaveCancelBar dirty={form.dirty} saving={formSaving} onSave={saveForm} onCancel={form.reset} lang={lang} testid="comp-save-bar" />
            {!gate.ok && (
              <div className="text-xs text-red-700" data-testid="comp-guard-reasons">
                {gate.reasons.map((r) => t(`comp.reasons.${r}`) || r).join(" · ")}
              </div>
            )}
            <div className="flex justify-end">
              <Button data-testid="comp-mark-complete"
                       disabled={form.dirty || td.dirty || (!doc.completed && !gate.ok)}
                       onClick={() => patch({ completed: !doc.completed })}>
                {doc.completed ? "Unmark" : t("comp.markComplete")}
              </Button>
            </div>
          </div>
        )}
    </FullScreenDialog>
  );
}
